import { useMemo } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatHuf } from "@/components/SalaryFormSheet";
import { formatDateHu } from "@/components/DeductionFormDialog";
import { cn } from "@/lib/utils";
import { Th, Td } from "../tabHelpers";

const CHIP_TONES = {
  neutral: "border-border bg-muted/40 text-foreground",
  blue: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/25 dark:bg-blue-500/10 dark:text-blue-300",
  amber:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-300",
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300",
};

function StatChip({ label, value, tone = "neutral", testId }) {
  return (
    <span
      data-testid={testId}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px]",
        CHIP_TONES[tone] || CHIP_TONES.neutral
      )}
    >
      <span className="font-mono font-semibold">{value}</span>
      <span className="opacity-70">{label}</span>
    </span>
  );
}

/**
 * Generic "earnings" section used for both Bónusz and Jutalék records.
 * The section header sits OUTSIDE the card so each block reads as a distinct
 * editorial section (Stripe-style), while the table card itself remains
 * uniform and minimal.
 */
export function EarningsSection({
  items,
  loading,
  onCreate,
  onDelete,
  onSelect,
  StatusBadge,
  Icon, // lucide icon component (rendered as a muted glyph next to the title)
  testIdPrefix, // "bonus" | "commission"
  labels,
}) {
  const totals = useMemo(() => {
    return items.reduce(
      (acc, it) => {
        const amt = Number(it.amount || 0);
        acc.total += amt;
        if (it.status === "approved") acc.approved += amt;
        if (it.status === "waiting_approval") acc.pending += amt;
        if (it.status === "paid") acc.paid += amt;
        return acc;
      },
      { total: 0, approved: 0, pending: 0, paid: 0 }
    );
  }, [items]);

  return (
    <section
      data-testid={`${testIdPrefix}es-section`}
      className="space-y-5"
    >
      {/* Editorial header — sits OUTSIDE the card. */}
      <header className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-muted-foreground/80">
              {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={2} />}
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
                {labels.eyebrow}
              </span>
            </div>
            <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-foreground sm:text-[22px]">
              {labels.sectionTitle}
            </h2>
          </div>
          <Button
            size="sm"
            data-testid={`new-${testIdPrefix}-button`}
            onClick={onCreate}
            className="self-start"
          >
            <Plus className="h-4 w-4" />
            {labels.newButton}
          </Button>
        </div>

        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <StatChip
              testId={`${testIdPrefix}-stat-count`}
              label={labels.itemNoun}
              value={`${items.length} db`}
              tone="neutral"
            />
            <StatChip
              testId={`${testIdPrefix}-stat-approved`}
              label="kifizetésre vár"
              value={`${formatHuf(totals.approved)} Ft`}
              tone="blue"
            />
            {totals.pending > 0 && (
              <StatChip
                testId={`${testIdPrefix}-stat-pending`}
                label="jóváhagyásra vár"
                value={`${formatHuf(totals.pending)} Ft`}
                tone="amber"
              />
            )}
            {totals.paid > 0 && (
              <StatChip
                testId={`${testIdPrefix}-stat-paid`}
                label="kifizetve"
                value={`${formatHuf(totals.paid)} Ft`}
                tone="emerald"
              />
            )}
          </div>
        )}
      </header>

      {/* Body card — uniform, minimal. */}
      <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {labels.loadingText}
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid={`${testIdPrefix}es-empty-state`}
            className="flex flex-col items-center gap-3 px-6 py-14 text-center"
          >
            <div className="grid h-11 w-11 place-items-center rounded-full border border-border bg-background text-muted-foreground/80">
              {Icon && <Icon className="h-4 w-4" strokeWidth={1.75} />}
            </div>
            <p className="text-sm font-medium">{labels.emptyTitle}</p>
            <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">
              {labels.emptyDescription}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 h-8"
              onClick={onCreate}
            >
              <Plus className="h-3.5 w-3.5" /> {labels.newButton}
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${testIdPrefix}es-table`}>
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th className="text-right">Bruttó összeg (Ft)</Th>
                  <Th>Kiadás ideje</Th>
                  <Th>Kiadó</Th>
                  <Th>Indok</Th>
                  <Th>Állapot</Th>
                  <Th className="pr-4 text-right">Műveletek</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it._id}
                    data-testid={`${testIdPrefix}-row-${it._id}`}
                    onClick={() => onSelect && onSelect(it)}
                    className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                  >
                    <Td className="text-right font-mono text-[12.5px] font-medium">
                      {formatHuf(it.amount)}
                    </Td>
                    <Td className="text-muted-foreground">
                      {formatDateHu(it.issued_at)}
                    </Td>
                    <Td>{it.issuer}</Td>
                    <Td className="max-w-[280px]">
                      <span className="line-clamp-2 text-[12.5px] text-muted-foreground">
                        {it.reason || "—"}
                      </span>
                    </Td>
                    <Td>
                      <StatusBadge value={it.status} />
                    </Td>
                    <Td className="pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {it.status === "waiting_approval" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                data-testid={`${testIdPrefix}-actions-${it._id}`}
                                aria-label={labels.menuAriaLabel}
                                className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`delete-${testIdPrefix}-${it._id}`}
                                onClick={() => onDelete(it)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Törlés
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
